/**
 * Responsibility: Implement npc actor behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
// ============================================================
// ENGINE/ENTITIES/NPC-ACTOR.TS — Room-resident NPC entity
// Lightweight: no full stat block. Infers combat stats from
// its identity archetype string.
//
// Named NpcActor (not NpcEntity) to avoid collision with the
// NpcEntity class in permissions.ts (am-i-allowed integration).
// ============================================================

import type { NPC } from "@anyquest/shared-core"
import { WorldObject } from './world-object'
type Severity = 'weak' | 'normal' | 'strong'
const STRONG_PATTERN = /dragon|demon|warlord|titan|champion|boss|elder|archmage|lich|golem/
const WEAK_PATTERN   = /child|beggar|peasant|civilian|merchant|innkeeper|hermit|scholar/
const BASE_DAMAGE: Record<Severity, number> = { weak: 4, normal: 9, strong: 18 }
export class NpcActor extends WorldObject {
  private readonly dto: NPC
  constructor(dto: NPC) {
    super()
    this.dto = dto
  }

  static fromDTO(dto: NPC): NpcActor {
    return new NpcActor(dto)
  }

  get id(): string { return this.dto.name; }

  examine(): string {
    return `${this.dto.name} — ${this.dto.lore}`
  }

  /** Classify threat level from identity archetype string. */
  severity(): Severity {
    const s = this.dto.identity.toLowerCase()
    if (STRONG_PATTERN.test(s)) return 'strong'
    if (WEAK_PATTERN.test(s))   return 'weak'
    return 'normal'
  }

  /** Infer effective DEX from threat level (NPCs have no stat block). */
  inferDex(): number {
    const s = this.severity()
    return s === 'strong' ? 14 : s === 'weak' ? 7 : 10
  }

  /**
   * Compute vitality damage dealt to the player on a d20 roll.
   * Returns 0 on a natural miss (d20 ≤ 4); capped at 35 per hit.
   */
  attackDamage(d20Roll: number): number {
    if (d20Roll <= 4) return 0
    const base = BASE_DAMAGE[this.severity()]
    const raw = base + Math.floor(d20Roll / 3)
    return Math.min(raw, 35)
  }

  toDTO(): NPC {
    return this.dto
  }
}
